import { useEffect, useRef, useState } from "react";

import {
  currentEntryStage,
  EXPLAIN_AFTER_MS,
  stageIsSlow,
} from "@/engine/diagnostics/entryTiming";
import { useEntryTiming } from "@/hooks/useEntryTiming";

/** Redraw often enough for a tenth of a second to move, and no more. */
const TICK_MS = 100;

/**
 * Where a walk into a room stands: which waits have passed, which one is
 * being waited on, how long the walk has taken, and whether it has stopped
 * looking ordinary. Every screen that draws the walk reads it from here, so
 * they agree on the moment the way out is offered.
 */
export function useEntryWait() {
  const timing = useEntryTiming();
  const [elapsed, setElapsed] = useState(0);
  // The press, remembered once. Every stage mark publishes a fresh timing
  // object, and a start time recomputed from that object would restart the
  // count at each step — which also pushed the slow notice, and the way out
  // with it, past the moment it was meant to appear.
  const startedAt = useRef<number | null>(null);
  const waiting = Boolean(timing) && !timing?.done;

  useEffect(() => {
    if (!waiting) {
      startedAt.current = null;
      return;
    }
    startedAt.current ??= Date.now();
    const started = startedAt.current;
    setElapsed(Date.now() - started);
    const id = window.setInterval(
      () => setElapsed(Date.now() - started),
      TICK_MS,
    );
    return () => window.clearInterval(id);
  }, [waiting]);

  const marks = timing?.marks ?? {};
  return {
    marks,
    current: currentEntryStage(marks),
    elapsed,
    counting: elapsed >= EXPLAIN_AFTER_MS,
    slow: !timing?.done && stageIsSlow(marks, elapsed),
  };
}
